import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { CrewMember } from "@/mocks/admin/crew";

type Props = {
  member: CrewMember;
};

function parseClockedIn(time: string): Date | null {
  if (!time) return null;
  const [h, m] = time.split(":").map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  const now = new Date();
  const d = new Date(now);
  d.setHours(h, m, 0, 0);
  // if it's in the "future" (clocked in tomorrow morning?), roll back a day
  if (d.getTime() > now.getTime()) d.setDate(d.getDate() - 1);
  return d;
}

function fmtElapsed(start: Date, now: Date) {
  const ms = Math.max(0, now.getTime() - start.getTime());
  const totalMin = Math.floor(ms / 60000);
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  const s = Math.floor((ms / 1000) % 60);
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

export default function StatusRibbon({ member }: Props) {
  const isClockedIn = !!(member.clockedInAt && member.currentProject);
  const start = isClockedIn ? parseClockedIn(member.clockedInAt!) : null;
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    if (!isClockedIn) return;
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, [isClockedIn]);

  if (isClockedIn && start) {
    const elapsed = fmtElapsed(start, now);
    const totalMin = Math.floor((now.getTime() - start.getTime()) / 60000);
    const earnedNow = ((totalMin / 60) * member.hourlyRate).toFixed(2);
    return (
      <div className="relative overflow-hidden rounded-lg border border-emerald-200 bg-emerald-50">
        <div className="absolute inset-y-0 left-0 w-1.5 bg-emerald-500"></div>
        <div className="pl-5 pr-4 py-3 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <span className="relative flex w-3 h-3 shrink-0">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-3 w-3 bg-emerald-500"></span>
            </span>
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-wider font-bold text-emerald-700">
                On the clock
              </div>
              <div className="text-sm font-bold text-ink-900 truncate">
                Working on{" "}
                <Link
                  to={`/projects/${member.currentProject}`}
                  className="font-mono text-emerald-700 hover:underline cursor-pointer"
                >
                  {member.currentProject}
                </Link>{" "}
                · clocked in at{" "}
                <span className="font-mono">{member.clockedInAt}</span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-4 md:gap-6 shrink-0">
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-wider font-bold text-emerald-700">
                Elapsed
              </div>
              <div className="font-mono text-lg font-bold text-ink-900 tabular-nums">
                {elapsed}
              </div>
            </div>
            <div className="hidden sm:block w-px h-9 bg-emerald-200"></div>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-wider font-bold text-emerald-700">
                Earned
              </div>
              <div className="font-mono text-lg font-bold text-ink-900 tabular-nums">
                ${earnedNow}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (member.status === "on-leave") {
    return (
      <RibbonShell
        color="amber"
        title="On Leave"
        subtitle={`${member.name.split(" ")[0]} is currently unavailable for scheduling.`}
        icon="ri-suitcase-line"
      />
    );
  }

  if (member.status === "training") {
    return (
      <RibbonShell
        color="indigo"
        title="In Training"
        subtitle={`${member.name.split(" ")[0]} is shadowing senior crew. Limited dispatch.`}
        icon="ri-graduation-cap-line"
      />
    );
  }

  if (member.status === "off") {
    return (
      <RibbonShell
        color="slate"
        title="Off Today"
        subtitle={`No shift scheduled. Last hours logged ${member.weeklyHours}h this week.`}
        icon="ri-moon-line"
      />
    );
  }

  return (
    <RibbonShell
      color="slate"
      title="Off Duty"
      subtitle={`Not clocked in. ${member.weeklyHours}h of ${member.weeklyTarget}h target this week.`}
      icon="ri-pause-circle-line"
    />
  );
}

function RibbonShell({
  color,
  title,
  subtitle,
  icon,
}: {
  color: "amber" | "indigo" | "slate";
  title: string;
  subtitle: string;
  icon: string;
}) {
  const tone = {
    amber: { bar: "bg-amber-500", bg: "bg-amber-50", border: "border-amber-200", text: "text-amber-700", icon: "bg-amber-100 text-amber-700" },
    indigo: { bar: "bg-indigo-500", bg: "bg-indigo-50", border: "border-indigo-200", text: "text-indigo-700", icon: "bg-indigo-100 text-indigo-700" },
    slate: { bar: "bg-ink-400", bg: "bg-cream-100", border: "border-ink-200", text: "text-ink-700", icon: "bg-white text-ink-600 border border-ink-200" },
  }[color];

  return (
    <div className={`relative overflow-hidden rounded-lg border ${tone.border} ${tone.bg}`}>
      <div className={`absolute inset-y-0 left-0 w-1.5 ${tone.bar}`}></div>
      <div className="pl-5 pr-4 py-3 flex items-center gap-3">
        <div className={`w-9 h-9 rounded-md flex items-center justify-center ${tone.icon}`}>
          <i className={`${icon} text-base`}></i>
        </div>
        <div className="min-w-0">
          <div className={`text-[10px] uppercase tracking-wider font-bold ${tone.text}`}>
            {title}
          </div>
          <div className="text-sm font-semibold text-ink-900 truncate">{subtitle}</div>
        </div>
      </div>
    </div>
  );
}